import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ApiError, api } from '../../lib/api'
import { useAuth } from '../../context/AuthContext'
import { AlreadySignedInNotice } from '../../components/AlreadySignedInNotice'

export default function ProviderSignup() {
  const navigate = useNavigate()
  const { me, loginWithToken } = useAuth()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  if (me) return <AlreadySignedInNotice continueAs="a new provider account" />

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (!acceptedTerms) {
      setError('You must agree to the Terms of Service and acknowledge the Privacy Policy to continue.')
      return
    }
    setBusy(true)
    try {
      const result = await api('/signup/provider', {
        method: 'POST',
        body: JSON.stringify({ first_name: firstName, last_name: lastName, email, password, accepted_terms: acceptedTerms }),
      })
      await loginWithToken(result.access_token)
      // Account creation is complete — continue straight into onboarding.
      // Never the dashboard: onboarding is this account's first authenticated experience.
      navigate('/practice/provider/onboarding', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message || 'Could not create your account.' : 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-shell warm-theme">
      <div className="auth-card">
        <h1>Set up your provider account</h1>
        <p className="muted" style={{ fontSize: 13.5 }}>
          For individual therapists signing up on their own — no practice or invite required.
        </p>
        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="field"><label>First name</label><input className="inp" value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus /></div>
            <div className="field"><label>Last name</label><input className="inp" value={lastName} onChange={(e) => setLastName(e.target.value)} required /></div>
          </div>
          <div className="field"><label>Email</label><input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <div className="field"><label>Password</label><input className="inp" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} /></div>
          <div className="field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
            <input type="checkbox" id="acceptedTerms" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} style={{ marginTop: 3 }} aria-required="true" />
            <label htmlFor="acceptedTerms" style={{ margin: 0, fontSize: 13.5 }}>
              I have read and agree to the <Link to="/terms" target="_blank">Terms of Service</Link> and acknowledge the <Link to="/privacy" target="_blank">Privacy Policy</Link>.
            </label>
          </div>
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="btn full" type="submit" disabled={busy}>{busy ? 'Creating your account…' : 'Continue'}</button>
        </form>
        <p className="muted" style={{ fontSize: 13, marginTop: 16 }}>
          Setting up a group practice or clinic? <Link to="/signup/practice">Sign up here</Link>
          <br />
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
